import { gql } from '@apollo/client';
import classNames from 'classnames';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';
import useAppSelector from '../../app/redux/useAppSelector';
import PopOver from '../../components/deprecated/PopOver';
import PopOverContainer from '../../components/deprecated/PopOverContainer';
import SVGIcon from '../../components/SVGIcon';
import Tooltip from '../../components/Tooltip';
import type { ThreadReactionList_ReactionsFragment } from '../../graphql/generated/graphqlTypes';
import addIcon from '../../icons/add.svg';
import UIActions from '../../ui/UIActions';
import getUserNames from '../../user/getUserNames';
import Emoji from './Emoji';
import EmojiPicker from './EmojiPicker';
import groupReactions from './groupReactions';
import styles from './ThreadReactionList.module.scss';

const REACTIONS_FRAGMENT = gql`
  fragment ThreadReactionList_reactions on Reaction {
    id
    emoji
    author {
      id
      ...getUserNames_user
    }
    ...groupReactions_threadReactions
  }

  ${groupReactions.fragments.threadReactions}
  ${getUserNames.fragments.user}
`;

interface ThreadReactionListProps {
  readOnly?: boolean;
  reactions: ThreadReactionList_ReactionsFragment[];
  onAdd?: (emoji: string) => void;
  onRemove?: (id: string) => void;
}

const ThreadReactionList = ({
  readOnly,
  reactions,
  onAdd,
  onRemove,
}: ThreadReactionListProps) => {
  const dispatch = useDispatch();
  const currentUser = useAppSelector((state) => state.session.user);
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState(false);

  return (
    <div className={styles.container}>
      {groupReactions(reactions, currentUser.id)
        .sort((a, b) => b.items.length - a.items.length)
        .map(({ emoji, items, activatedId }) => {
          const pill = (
            <div
              className={classNames(styles.pill, {
                [styles.activated]: activatedId,
              })}
            >
              <Emoji char={emoji} />
              {items.length > 1 && (
                <div className={styles.count}>{items.length}</div>
              )}
            </div>
          );
          return (
            <div key={emoji} className={styles.item}>
              <Tooltip
                tooltip={getUserNames(
                  items.map(({ author }) => author),
                  { currentUser, limit: 20 },
                )}
                trigger="hover"
                placement="top"
                popOverPortal={document.body}
              >
                {readOnly ? (
                  pill
                ) : (
                  <button
                    type="button"
                    onClick={() =>
                      activatedId ? onRemove?.(activatedId) : onAdd?.(emoji)
                    }
                  >
                    {pill}
                  </button>
                )}
              </Tooltip>
            </div>
          );
        })}
      {!readOnly && (
        // @ts-expect-error
        <PopOver
          display="inline-block"
          placement="bottom-end"
          open={isEmojiPickerOpen}
          onOpenChange={(newOpen) => {
            setIsEmojiPickerOpen(newOpen);
            return newOpen;
          }}
          disablePersist
          preventCloseOnContentClick
          trigger="click"
          popOver={
            <PopOverContainer noPadding>
              <EmojiPicker
                onPick={(emoji) => {
                  const reaction = reactions.find(
                    (item) =>
                      item.emoji === emoji && item.author.id === currentUser.id,
                  );
                  if (reaction) {
                    onRemove?.(reaction.id);
                  } else {
                    dispatch(UIActions.addToRecentlyUsedEmojis(emoji));
                    onAdd?.(emoji);
                  }
                  setIsEmojiPickerOpen(false);
                }}
              />
            </PopOverContainer>
          }
          popOverPortal={document.body}
        >
          <button type="button">
            <div className={classNames(styles.pill, styles.plus)}>
              <span className={styles.icon}>
                <SVGIcon size={10}>{addIcon}</SVGIcon>
              </span>
            </div>
          </button>
        </PopOver>
      )}
    </div>
  );
};

ThreadReactionList.fragments = { reactions: REACTIONS_FRAGMENT };

export default ThreadReactionList;
